import { useState, type ReactNode } from "react";

import { Table, TableBody, TableCell, TableContainer, TableRow } from "ui/table";
import { Stack } from "ui/stack";
import { Typography } from "ui/typography";
import toast from "components/partials/toast/toast";
import CollegeProfileEditActions from "containers/admin/clients/client/college-profile-page/college-profile-edit-actions";
import CollegeProfileGeneralDetailsForm, {
  CORE_TEAM_FIELDS,
} from "containers/admin/clients/client/college-profile-page/college-profile-general-details-form";
import {
  DIGITAL_CHANNELS_OPTIONS,
  FORM_DATA_CAPTURE_PAGE_OPTIONS,
  MARKETING_CHANNELS_OPTIONS,
  PARTNER_APPROVAL_OPTIONS,
  PRINT_OPTIONS,
} from "containers/admin/clients/client/college-profile-page/college-profile-general-details-form.helpers";
import type { GeneralDetailsOption } from "containers/admin/clients/client/college-profile-page/college-profile-general-details-form.helpers";
import CollegeProfileSectionHeader from "containers/admin/clients/client/college-profile-page/college-profile-section-header";

import type { ClientOverviewDTO } from "generated/schemas";

import { useCurrentClient, usePartiallyUpdateClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import { getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

const FORM_ID = "college-profile-general-details-form";

const joinSelectedLabels = (
  options: GeneralDetailsOption[],
  values: string[] | undefined,
): string =>
  options
    .filter((option) => (values ?? []).includes(option.value))
    .map((option) => option.label)
    .join(", ");

const findLabel = (
  options: GeneralDetailsOption[],
  value: string | undefined,
): string | undefined => options.find((option) => option.value === value)?.label;

type LabelValueRowProps = { label: string; value: ReactNode };

const LabelValueRow = ({ label, value }: LabelValueRowProps) => (
  <Stack spacing={0}>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: "grey.900", fontWeight: 500 }}>
      {value}
    </Typography>
  </Stack>
);

export type CollegeProfileGeneralDetailsSectionProps = {
  isEditing: boolean;
  onEditingChange: (isEditing: boolean) => void;
};

const CollegeProfileGeneralDetailsSection = ({
  isEditing,
  onEditingChange,
}: CollegeProfileGeneralDetailsSectionProps) => {
  const client = useCurrentClient();
  const currentUser = useCurrentUser();
  const patchClientMutation = usePartiallyUpdateClient();
  const [isFormDirty, setIsFormDirty] = useState(false);

  const canWriteClient = hasPermission(currentUser, Permission.PERM_CLIENT_WRITE);
  const overview = client.overview;

  const exitEditMode = () => {
    setIsFormDirty(false);
    onEditingChange(false);
  };

  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: isFormDirty,
    onDiscard: exitEditMode,
  });

  const handleEdit = () => onEditingChange(true);

  const handleSubmit = ({ name, overview: updatedOverview }: { name: string; overview: ClientOverviewDTO }) => {
    patchClientMutation.mutate(
      { id: client.id, data: { name, overview: updatedOverview } },
      {
        onSuccess: () => {
          toast.success({ data: { title: "Client overview saved" } });
          exitEditMode();
        },
        onError: (err) => {
          toast.error({
            data: {
              title: "Could not save client overview",
              message: getErrorMessage(err),
            },
          });
        },
      },
    );
  };

  const partnerApprovalLabel = findLabel(PARTNER_APPROVAL_OPTIONS, overview?.partnerApproval);
  const channelsLabel = joinSelectedLabels(
    MARKETING_CHANNELS_OPTIONS,
    overview?.marketingChannels?.channels,
  );
  const formDataCapturePageLabel = joinSelectedLabels(
    FORM_DATA_CAPTURE_PAGE_OPTIONS,
    overview?.marketingChannels?.formDataCapturePage,
  );
  const digitalChannelsLabel = joinSelectedLabels(
    DIGITAL_CHANNELS_OPTIONS,
    overview?.marketingChannels?.digitalChannels,
  );
  const printLabel = joinSelectedLabels(PRINT_OPTIONS, overview?.marketingChannels?.print);
  const formDataCapturePageOtherDetails = overview?.marketingChannels?.formDataCapturePageOtherDetails;
  const printOtherDetails = overview?.marketingChannels?.printOtherDetails;
  const hasMarketingChannelsData = Boolean(
    channelsLabel ||
      formDataCapturePageLabel ||
      digitalChannelsLabel ||
      printLabel ||
      formDataCapturePageOtherDetails ||
      printOtherDetails,
  );
  const filledCoreTeamFields = CORE_TEAM_FIELDS.filter((field) => overview?.coreTeam?.[field.name]);

  return (
    <Stack spacing={0.75}>
      {unsavedChangesDialog}
      <CollegeProfileSectionHeader title="Client overview">
        {canWriteClient && (
          <CollegeProfileEditActions
            isEditing={isEditing}
            isSaving={patchClientMutation.isPending}
            onEdit={handleEdit}
            onCancel={handleCancelClick}
            submitFormId={isEditing ? FORM_ID : undefined}
          />
        )}
      </CollegeProfileSectionHeader>

      {isEditing ? (
        <CollegeProfileGeneralDetailsForm
          formId={FORM_ID}
          clientName={client.name}
          overview={overview}
          onSubmit={handleSubmit}
          onDirtyChange={setIsFormDirty}
        />
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 600 }}>
          <LabelValueRow label="Client name" value={client.name} />

          {partnerApprovalLabel && (
            <LabelValueRow label="Do we have partner approval?" value={partnerApprovalLabel} />
          )}

          {hasMarketingChannelsData && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" component="h4" sx={{ color: "grey.900" }}>
                Marketing channels
              </Typography>
              {channelsLabel && <LabelValueRow label="Channels" value={channelsLabel} />}
              {formDataCapturePageLabel && (
                <LabelValueRow label="Form data capture page" value={formDataCapturePageLabel} />
              )}
              {formDataCapturePageOtherDetails && (
                <LabelValueRow label="Other details" value={formDataCapturePageOtherDetails} />
              )}
              {digitalChannelsLabel && (
                <LabelValueRow label="Digital channels" value={digitalChannelsLabel} />
              )}
              {printLabel && <LabelValueRow label="Print" value={printLabel} />}
              {printOtherDetails && <LabelValueRow label="Other details" value={printOtherDetails} />}
            </Stack>
          )}

          {filledCoreTeamFields.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" component="h4" sx={{ color: "grey.900" }}>
                Core team
              </Typography>
              <TableContainer
                sx={{
                  // `border-radius` has no effect on table cells under `border-collapse: collapse`
                  // (true in every browser, not a MUI quirk), so a real curved corner needs collapse
                  // off — which brings back the doubling problem, avoided here by letting each edge
                  // draw from exactly one side: the container owns top+left, every cell owns
                  // bottom+right. That covers all four outer edges and every internal divider once
                  // each, with no row/column bookkeeping needed for the borders themselves.
                  borderTop: 1,
                  borderLeft: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  "& table": { borderCollapse: "separate", borderSpacing: 0 },
                  "& .MuiTableCell-root": {
                    borderBottom: 1,
                    borderRight: 1,
                    borderColor: "divider",
                    py: 1,
                    px: 2,
                  },
                }}
              >
                <Table>
                  <TableBody>
                    {filledCoreTeamFields.map((field, index) => {
                      const isFirstRow = index === 0;
                      const isLastRow = index === filledCoreTeamFields.length - 1;

                      return (
                        <TableRow key={field.name}>
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              width: "50%",
                              // Top-left is the container's corner (its own border draws that edge);
                              // only the bottom-left needs rounding here.
                              borderBottomLeftRadius: isLastRow ? 8 : 0,
                            }}
                          >
                            {field.label}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "grey.900",
                              fontWeight: 500,
                              borderTopRightRadius: isFirstRow ? 8 : 0,
                              borderBottomRightRadius: isLastRow ? 8 : 0,
                            }}
                          >
                            {overview?.coreTeam?.[field.name]}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default CollegeProfileGeneralDetailsSection;
